import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Lock, Eye, Server, Key, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Logo } from "@/components/ui/logo";

const securityPrinciples = [
  {
    icon: Lock,
    title: "Encryption at Rest & In Transit",
    description:
      "All data is encrypted using AES-256 at rest and TLS 1.3 in transit. Database connections are secured via SSL certificates. API keys and secrets are stored in encrypted vaults, never in plaintext.",
  },
  {
    icon: Eye,
    title: "Zero-Retention Execution",
    description:
      "Requirements text submitted for review is processed in ephemeral containers that are destroyed after each run. Enterprise customers can enable strict zero-retention mode where no input data is persisted beyond the active session.",
  },
  {
    icon: Server,
    title: "Infrastructure Security",
    description:
      "Reqqy runs on SOC 2 Type II certified cloud infrastructure with automated vulnerability scanning, intrusion detection, and 24/7 monitoring. All infrastructure components are deployed in isolated VPCs with strict network segmentation.",
  },
  {
    icon: Key,
    title: "Authentication & Access Control",
    description:
      "User authentication is secured with bcrypt password hashing, email verification, and rate-limited login attempts. Organisation-level role-based access control (RBAC) ensures users can only access resources they're authorised to view.",
  },
];

const complianceItems = [
  {
    title: "SOC 2 Type II",
    description:
      "Our infrastructure provider maintains SOC 2 Type II certification, with annual audits covering security, availability, and confidentiality.",
  },
  {
    title: "GDPR Compliant",
    description:
      "Full compliance with the EU General Data Protection Regulation including data subject rights, lawful basis for processing, and cross-border transfer safeguards.",
  },
  {
    title: "Data Residency",
    description:
      "Customer data is stored in the EU (Frankfurt) by default. Enterprise customers can choose from additional regions including US-East, US-West, and Asia-Pacific.",
  },
  {
    title: "Sub-Processor Management",
    description:
      "We maintain a transparent list of sub-processors and notify customers of any changes. All sub-processors are contractually bound to equivalent security and privacy standards.",
  },
];

const securityFeatures = [
  "Row Level Security (RLS) on all database tables",
  "Default-deny access policies — explicit grants required",
  "Input sanitisation and parameterised queries to prevent injection",
  "Rate limiting on all public endpoints",
  "Account enumeration protection on auth endpoints",
  "CORS origin validation with environment-based configuration",
  "Stripe webhook signature verification",
  "Generic error messages to prevent information leakage",
  "Automated session management with secure token rotation",
  "Content Security Policy (CSP) headers",
];

export default function Security() {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Security"
        description="Learn how Reqqy protects your data with encryption, zero-retention execution, SOC 2 infrastructure, and comprehensive access controls."
        canonical="/security"
      />

      {/* Header */}
      <header className="border-b border-border">
        <div className="container flex items-center justify-between h-16 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo variant="full" className="h-8 w-auto" />
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to="/home">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      <main className="container px-4 md:px-6 py-12 md:py-16 max-w-4xl">
        <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">Security</h1>
        <p className="text-lg text-muted-foreground mb-12">
          Your requirements contain sensitive business logic and strategic direction. We take protecting that data
          seriously.
        </p>

        {/* Principles */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Security Principles</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {securityPrinciples.map(({ icon: Icon, title, description }) => (
              <div key={title} className="p-5 rounded-xl border border-border bg-card">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Application Security */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Application Security</h2>
          <div className="p-6 rounded-xl border border-border bg-card">
            <p className="text-muted-foreground mb-4">
              Reqqy follows a defence-in-depth approach with multiple layers of security controls:
            </p>
            <div className="grid gap-2 md:grid-cols-2">
              {securityFeatures.map((feature) => (
                <div key={feature} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Data Handling */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Data Handling</h2>
          <div className="space-y-4">
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">No Training on Customer Data</h3>
              <p className="text-sm text-muted-foreground">
                Reqqy never uses your requirements, review results, or any other customer data to train AI models. Your
                data is yours — we process it solely to deliver the review service you requested.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Data Retention</h3>
              <p className="text-sm text-muted-foreground">
                Review results are stored in your account for as long as you need them. You can delete individual runs,
                entire projects, or your full account at any time. Deletion is permanent and propagates to all backups
                within 30 days.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">Temporary Mode</h3>
              <p className="text-sm text-muted-foreground">
                For sensitive reviews, enable Temporary Mode. In this mode, your requirements and results are stored
                only for the duration of your active session and are automatically purged when you close the review. No
                data is persisted to the database.
              </p>
            </div>
          </div>
        </section>

        {/* Compliance */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Compliance & Certifications</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {complianceItems.map(({ title, description }) => (
              <div key={title} className="p-5 rounded-xl border border-border bg-card">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">{title}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Vulnerability Reporting */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Vulnerability Reporting</h2>
          <div className="p-6 rounded-xl border border-border bg-card">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-muted-foreground mb-3">
                  If you discover a security vulnerability, please report it responsibly. We appreciate the security
                  research community and will work with you to address any findings.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>
                    • Email:{" "}
                    <a href="mailto:security@reqqy.app" className="text-primary hover:underline">
                      security@reqqy.app
                    </a>
                  </li>
                  <li>• Please include detailed steps to reproduce the vulnerability</li>
                  <li>• We aim to acknowledge reports within 24 hours and resolve critical issues within 72 hours</li>
                  <li>• We do not pursue legal action against good-faith security researchers</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="p-8 rounded-xl border border-border bg-muted/50 text-center">
          <h2 className="text-xl font-semibold mb-2">Have security questions?</h2>
          <p className="text-muted-foreground mb-4">
            Our security team is available to answer questions and provide additional documentation for enterprise
            evaluations.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild>
              <a href="mailto:security@reqqy.app">Contact Security Team</a>
            </Button>
            <Button asChild variant="outline">
              <Link to="/dpa">
                View DPA
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
